import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import * as SituationAPI from '../../../services/SituationServices';
import GroupBody from './GroupBody';
import SituationBody from './SituationBody';
import ChoicesBody from './ChoicesBody';
import UserSubmittedBody from './UserSubmittedBody';
import FreeResponseBody from './FreeResponseBody';
import styles from './styles/ViewSituation.module.css';
import { useNavigate } from 'react-router-dom';
import { useScenario } from '../../../context/ScenarioContext';
import FeedbackModal from './FeedbackModal';
import { FaCommentDots } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import ViewCanvas from './ViewCanvas';
import { FaExpand, FaCompress } from 'react-icons/fa';
import Loading from '../../loading/Loading';


const ViewSituation = ({
    userId,
    scenario,
    canvas,
    currentSituationId,
    setCurrentSituationId,
    preview,
    userLoading,
    userKeys,
    applyKeyChanges,
    clearScenarioKeys,
    assignmentId,
    fromToC,
    handleFromToC, //boolean setter
    universalLock,
    userLock,
    setVisitedSituationsPreview, //Not sure what this is doing at the moment
    /**
     * Where a situation comes from. Defaults to the published read model, which
     * is what every player path wants. The editor's preview passes a source
     * backed by the in-memory draft instead (utils/draftPlayAdapter.js), so an
     * author previews the edits they just made rather than the last published
     * version. Everything below this line is identical either way.
     */
    situationSource = SituationAPI.getSituation,
}) => {
    const [currentSituation, setCurrentSituationInternal] = useState(null);
    const [isLoadingSituation, setIsLoadingSituation] = useState(false);
    const navigate = useNavigate();
    const { submitDecision } = useScenario();
    const [feedbackText, setFeedbackText] = useState('');
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [isMinimapExpanded, setIsMinimapExpanded] = useState(false);

    // The id of the last situation we logged an arrival for. Used to skip
    // re-logging the same situation when the fetch effect re-runs (dep changes,
    // ToC fromToC toggle, remounts) without a real navigation. A genuine
    // non-consecutive revisit (A -> B -> A) still logs, since the ref holds the
    // intervening situation.
    const loggedSituationRef = useRef(null);
    // Guards so reaching an end node finalizes the run exactly once per arrival.
    const finalizedRef = useRef(false);
    // Tracks whether the situation currently on screen is an end node, so the
    // unmount cleanup can clear the resume pointer when we leave it.
    const isEndRef = useRef(false);
    // The situation whose keys were last applied. The fetch effect re-runs
    // without a real arrival (the fromToC toggle, dep changes); applying again
    // then would take back a key a hint choice on this situation just gave.
    const keysAppliedForRef = useRef(null);

    // The completion work that used to live behind the "Home" button. It now runs
    // the moment a logged scenario reaches an end node, so abandoning at the end
    // (navigating away without clicking Home) still completes the log and clears keys.
    const finalizeCompletion = useCallback(async () => {
        if (!scenario?._id) return;

        try {
            if (scenario.isLogged && !preview) {
                await submitDecision({
                    isCompleted: true,
                    assignmentId: assignmentId
                });
            }

            if (!preview) {
                await ScenarioAPI.incrementCompletions(scenario._id);
            }

            if (!preview && clearScenarioKeys) {
                await clearScenarioKeys();
            }
        } catch (error) {
            console.error(`Error during finish process: ${error}`);
        }
    }, [
        scenario,
        clearScenarioKeys,
        submitDecision,
        preview,
        assignmentId
    ]);

    useEffect(() => {
        const fetchCurrentSituation = async () => {
            if (!currentSituationId || !scenario?._id || userLoading) {
                if (!userLoading && !currentSituationId) {
                    setCurrentSituationInternal(null);
                }
                return;
            }

            setIsLoadingSituation(true);
            try {
                const situationData = await situationSource(currentSituationId);
                if (!situationData) throw new Error(`Situation ${currentSituationId} not found`);
                setCurrentSituationInternal(situationData);

                if (applyKeyChanges && keysAppliedForRef.current !== situationData._id) {
                    keysAppliedForRef.current = situationData._id;
                    await applyKeyChanges({
                        grants: situationData.grantsKeys,
                        removes: situationData.removesKeys,
                        source: { situationId: situationData._id },
                    });
                }


                if (scenario.isLogged && situationData.isLogged && !preview) {
                    // Only record the arrival once per actual arrival. Re-runs of this
                    // effect for the situation we're already on (e.g. the fromToC toggle
                    // below) must not push a duplicate 'situation' decision.
                    if (loggedSituationRef.current !== situationData._id) {
                        loggedSituationRef.current = situationData._id;
                        await submitDecision({
                            situationId: situationData._id,
                            type: 'situation',
                            nextSituationId: situationData._id,
                            fromToC: fromToC,
                            assignmentId: assignmentId
                        });
                    }

                    handleFromToC(false);
                }

                // Reaching an end node IS completion. Fire once per arrival, after the
                // situation visit above has been logged, so the log is flipped to
                // 'completed' regardless of whether the user clicks "Home".
                if (situationData.isEnd && scenario.isLogged && !preview) {
                    if (!finalizedRef.current) {
                        finalizedRef.current = true;
                        await finalizeCompletion();
                    }
                } else {
                    // Re-arm for replays (e.g. after Restart) once we're off the end node.
                    finalizedRef.current = false;
                }

            } catch (error) {
                console.error(`Error fetching situation: ${error}`);
                setCurrentSituationInternal(null);
            } finally {
                setIsLoadingSituation(false);
            }
        };

        fetchCurrentSituation();
    }, [
        currentSituationId,
        scenario?._id,
        scenario?.isLogged,
        applyKeyChanges,
        submitDecision,
        preview,
        userLoading,
        assignmentId,
        fromToC,
        finalizeCompletion,
        situationSource
    ]);

    useEffect(() => {
        if (preview && currentSituationId) {
            setVisitedSituationsPreview(prev => [...new Set([...prev, currentSituationId])]);
        }
    }, [preview, currentSituationId]);
    

    // Keep a live flag of whether we're sitting on an end node, for the cleanup below.
    useEffect(() => {
        isEndRef.current = !!currentSituation?.isEnd;
    }, [currentSituation]);

    // When leaving the scenario from an end node (Home, browser back, or any route
    // change), clear the saved resume pointer so a return starts at the title page
    // instead of dropping back onto the final situation.
    useEffect(() => {
        return () => {
            if (isEndRef.current && !preview) {
                setCurrentSituationId(null);
            }
        };
    }, [preview, setCurrentSituationId]);

    // Completion already happened on arrival at the end node; "Home" is now just navigation.
    const handleFinish = useCallback(() => {
        setCurrentSituationId(null);
        navigate('/');
    }, [setCurrentSituationId, navigate]);

    const closeFeedbackModal = useCallback(() => {
        setShowFeedbackModal(false);
        setFeedbackText('');
    }, []);

    const handleToggleMinimap = useCallback(() => {
        setIsMinimapExpanded(prev => !prev);
    }, []);

    const renderConditionalBody = () => {
        if (!currentSituation) return null;

        if (currentSituation.isUserSubmitted) {
            return <UserSubmittedBody situation={currentSituation} setCurrentSituationId={setCurrentSituationId} isScenarioLogged={scenario?.isLogged} preview={preview} assignmentId = {assignmentId} universalLock={universalLock} userLock={userLock}/>;
        } else if (currentSituation.isFreeResponse) {
            return <FreeResponseBody situation={currentSituation} setCurrentSituationId={setCurrentSituationId} isScenarioLogged={scenario?.isLogged} preview={preview} assignmentId = {assignmentId} universalLock={universalLock} userLock={userLock}/>;
        } else if (currentSituation.choices && currentSituation.choices.length > 0) {
            return (
                <ChoicesBody
                    choices={currentSituation.choices}
                    setCurrentSituationId={setCurrentSituationId}
                    isScenarioLogged={scenario?.isLogged}
                    preview={preview}
                    scenario={scenario}
                    userKeys={userKeys}
                    applyKeyChanges={applyKeyChanges}
                    assignmentId={assignmentId}
                    universalLock={universalLock}
                    userLock={userLock}
                />
            );
        } else if (currentSituation.isEnd && !preview) {
            return <button className={styles.button} onClick={handleFinish}>Home</button>;
        }
        return null;
    };

    if (userLoading) {
        return <div className={styles.container}><Loading /></div>;
    }
    if (isLoadingSituation) {
        return <div className={styles.container}><Loading /></div>;
    }
    
    if (!currentSituationId || !currentSituation) {
        if (currentSituationId && !isLoadingSituation && !currentSituation) {
             return <div className={styles.loadingContainer}>Error loading data.</div>;
        }
        return <div className={styles.container}><Loading /></div>;
    }

    return (
        <div className={`${styles.container} ${preview ? styles.previewPadding : ''}`}>
            {currentSituation?.situationGroup && (
                <GroupBody group={currentSituation.situationGroup} />
            )}
    
            <SituationBody situation={currentSituation} />
            {renderConditionalBody()}
            
            {/* Minimap */}
            {(scenario?.showMap || preview) && canvas && (
                <div className={isMinimapExpanded ? styles.canvasExpanded : styles.canvasMinimap}>
                    <ViewCanvas canvas={canvas} currentSituationId={currentSituationId} isMinimapExpanded={isMinimapExpanded} />
                    <button type="button" aria-label={isMinimapExpanded ? "Compress minimap" : "Expand minimap"} className={styles.toggleButton} onClick={handleToggleMinimap}>
                        {isMinimapExpanded ? <FaCompress /> : <FaExpand />} 
                    </button>
                </div>
            )}

            {showFeedbackModal && (
                <FeedbackModal
                    userId={userId}
                    closeFeedbackModal={closeFeedbackModal}
                    feedbackText={feedbackText}
                    setFeedbackText={setFeedbackText}
                    scenario={scenario}
                    situation={currentSituation}
                    assignmentId={assignmentId}
                />
            )}

            {!preview && (
                <FaCommentDots 
                    className={styles.feedbackButton}
                    onClick={() => setShowFeedbackModal(true)}
                    data-tooltip-id="vs-tooltip"
                    data-tooltip-content="Submit Feedback"
                />
            )}
            
            <Tooltip id="vs-tooltip" place="left" style={{ zIndex: 9999 }} />
        </div>
    );
};

export default ViewSituation;